import { useState, useMemo } from 'react'
import { format, parseISO, differenceInDays, addYears, isAfter } from 'date-fns'
import { useBirthdays } from './hooks/useBirthdays'
import { Portal } from '../../components/Portal'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { cn } from '../../lib/utils'

const nextOccurrence = (birthDateStr) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const birth = parseISO(birthDateStr)
  const thisYear = new Date(today.getFullYear(), birth.getMonth(), birth.getDate())
  return isAfter(thisYear, today) || thisYear.getTime() === today.getTime()
    ? thisYear
    : addYears(thisYear, 1)
}

const daysUntil = (birthDateStr) => differenceInDays(nextOccurrence(birthDateStr), new Date())

const age = (birthDateStr) => {
  const birth = parseISO(birthDateStr)
  const next  = nextOccurrence(birthDateStr)
  return next.getFullYear() - birth.getFullYear()
}

// ── Birthday form modal ──────────────────────────────────────────────────────
const BirthdayModal = ({ birthday, onSave, onClose }) => {
  const [name,  setName]  = useState(birthday?.name       ?? '')
  const [date,  setDate]  = useState(birthday?.birth_date ?? '')
  const [notes, setNotes] = useState(birthday?.notes      ?? '')
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim() || !date) { setError('Name and date are required.'); return }
    setSaving(true)
    setError('')
    try {
      await onSave({ id: birthday?.id, name: name.trim(), birth_date: date, notes })
      onClose()
    } catch (err) {
      setError(err?.message ?? 'Could not save. Please try again.')
    } finally { setSaving(false) }
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0" onClick={onClose}>
        <div className="absolute inset-0 bg-black/40" />
        <form className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 shadow-card space-y-4"
          onClick={(e) => e.stopPropagation()} onSubmit={submit}>
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-text-primary">{birthday ? 'Edit Birthday' : 'Add Birthday'}</h2>
            <button type="button" onClick={onClose} className="text-text-muted p-1">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted mb-1 block">Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus required
              placeholder="e.g. Emma"
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted mb-1 block">Date of birth</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted mb-1 block">Notes (optional)</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Gift ideas, preferences…"
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}

          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" className="flex-1" disabled={saving}>
              {saving ? <Spinner size="sm" /> : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </Portal>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────
export const BirthdaysPage = () => {
  const { data: birthdays = [], isLoading, addBirthday, updateBirthday, deleteBirthday } = useBirthdays()
  const [editing, setEditing] = useState(null)   // birthday object | 'new'
  const [confirmDel, setConfirmDel] = useState(null)

  const sorted = useMemo(() => {
    return [...birthdays].sort((a, b) => daysUntil(a.birth_date) - daysUntil(b.birth_date))
  }, [birthdays])

  const handleSave = async (fields) => {
    if (fields.id) {
      await updateBirthday.mutateAsync(fields)
    } else {
      await addBirthday.mutateAsync(fields)
    }
  }

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-text-primary">Birthdays</h1>
        <button onClick={() => setEditing('new')}
          className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
          Add
        </button>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <div className="text-5xl mb-3">🎂</div>
          <p className="text-sm">No birthdays yet. Add one to get started!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((b) => {
            const days = daysUntil(b.birth_date)
            const upcoming = days <= 30
            return (
              <div key={b.id}
                className={cn('bg-surface rounded-xl border p-4 flex items-center gap-4',
                  days === 0 ? 'border-amber-300 bg-amber-50' : upcoming ? 'border-primary/30' : 'border-border')}>
                <div className="text-2xl flex-shrink-0">{days === 0 ? '🎉' : '🎂'}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-text-primary">{b.name}</span>
                    <span className="text-xs text-text-muted">
                      {format(parseISO(b.birth_date), 'MMMM d')} · turns {age(b.birth_date)}
                    </span>
                  </div>
                  <p className={cn('text-xs mt-0.5',
                    days === 0 ? 'font-bold text-amber-600' : days <= 7 ? 'text-primary font-medium' : 'text-text-muted')}>
                    {days === 0 ? 'Today! 🎉' : days === 1 ? 'Tomorrow' : `In ${days} days`}
                  </p>
                  {b.notes && <p className="text-xs text-text-muted mt-1 truncate">{b.notes}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditing(b)} className="text-text-muted hover:text-primary p-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  {confirmDel === b.id ? (
                    <div className="flex gap-1 items-center">
                      <button onClick={() => setConfirmDel(null)} className="text-xs text-text-muted">Cancel</button>
                      <button onClick={() => { deleteBirthday.mutate(b.id); setConfirmDel(null) }}
                        className="text-xs text-rose-500 font-semibold">Del</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDel(b.id)} className="text-text-muted hover:text-rose-500 p-1">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {editing && (
        <BirthdayModal
          birthday={editing === 'new' ? null : editing}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
